import { eventToDomainTopic, findTriggerableEvent } from "@nexus/contracts";

export interface ScheduledJobPublishPreview {
  /** Domain or full topic name shown in the preview. */
  topic: string;
  /** Convenience read of attributes.eventType (null when missing). */
  eventType: string | null;
  attributes: Record<string, string>;
  /** Compact body summary for orientation (not the server envelope). */
  bodySummary: string | null;
}

/**
 * Client-side preview of what curated create will publish. The server still
 * builds the full Pub/Sub target (project-qualified topic + attributes + body).
 */
export function deriveScheduledJobPreview(eventName: string): ScheduledJobPublishPreview | null {
  if (!findTriggerableEvent(eventName)) return null;
  return {
    topic: eventToDomainTopic(eventName),
    eventType: eventName,
    attributes: { eventType: eventName },
    bodySummary: null,
  };
}

/** Build a preview from Advanced form fields (create/edit power path). */
export function deriveAdvancedScheduledJobPreview(options: {
  topicName: string;
  attributesJson: string;
  bodyJson: string;
}): ScheduledJobPublishPreview {
  const attributes = parseAttributesJson(options.attributesJson) ?? {};
  const eventType =
    typeof attributes.eventType === "string" && attributes.eventType.trim().length > 0
      ? attributes.eventType.trim()
      : null;
  let bodySummary: string | null = null;
  const trimmedBody = options.bodyJson.trim();
  if (trimmedBody.length > 0) {
    try {
      bodySummary = JSON.stringify(JSON.parse(trimmedBody) as unknown);
    } catch {
      bodySummary = trimmedBody.slice(0, 120);
    }
  }
  return {
    topic: options.topicName.trim() || "—",
    eventType,
    attributes,
    bodySummary,
  };
}

export interface JsonErrorLocation {
  line: number;
  column: number;
}

export type AttributesJsonIssue =
  | { kind: "invalid-json"; at?: JsonErrorLocation }
  | { kind: "not-an-object" }
  | { kind: "non-string-values"; keys: string[] };

export type AttributesJsonCheck =
  | { ok: true; value: Record<string, string>; missingEventType: boolean }
  | { ok: false; issue: AttributesJsonIssue };

export type BodyJsonIssue =
  | { kind: "invalid-json"; at?: JsonErrorLocation }
  | { kind: "empty" };

export type BodyJsonCheck =
  | { ok: true; value: unknown }
  | { ok: false; issue: BodyJsonIssue };

export function parseAttributesJson(value: string): Record<string, string> | null {
  const check = checkAttributesJson(value);
  return check.ok ? check.value : null;
}

/**
 * Live Advanced validation for attributes — object of string values, with a
 * soft flag when `eventType` is missing (submit still requires it when Pub/Sub
 * is overridden).
 */
export function checkAttributesJson(value: string): AttributesJsonCheck {
  const trimmed = value.trim();
  if (!trimmed) {
    return { ok: true, value: {}, missingEventType: true };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (error) {
    return {
      ok: false,
      issue: { kind: "invalid-json", at: locateJsonError(value, error) },
    };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, issue: { kind: "not-an-object" } };
  }

  const out: Record<string, string> = {};
  const nonStringKeys: string[] = [];
  for (const [key, entry] of Object.entries(parsed)) {
    if (typeof entry !== "string") {
      nonStringKeys.push(key);
      continue;
    }
    out[key] = entry;
  }
  if (nonStringKeys.length > 0) {
    return { ok: false, issue: { kind: "non-string-values", keys: nonStringKeys } };
  }

  return {
    ok: true,
    value: out,
    missingEventType: !hasEventTypeAttribute(out),
  };
}

export function parseBodyJson(value: string): { ok: true; value: unknown } | { ok: false } {
  const check = checkBodyJson(value);
  return check.ok ? { ok: true, value: check.value } : { ok: false };
}

/** Live Advanced validation for the raw Pub/Sub body (any JSON value). */
export function checkBodyJson(value: string): BodyJsonCheck {
  const trimmed = value.trim();
  if (!trimmed) return { ok: false, issue: { kind: "empty" } };

  try {
    return { ok: true, value: JSON.parse(trimmed) as unknown };
  } catch (error) {
    return {
      ok: false,
      issue: { kind: "invalid-json", at: locateJsonError(value, error) },
    };
  }
}

/**
 * Re-indent typed JSON. Returns null when unparseable so Format stays disabled
 * and the status line owns the error.
 */
export function formatScheduledJobJsonText(text: string): string | null {
  try {
    return JSON.stringify(JSON.parse(text.trim() === "" ? "{}" : text), null, 2);
  } catch {
    return null;
  }
}

/**
 * Reads line/column from the engine message when present (V8/Firefox). Same
 * approach as Events publish — position is optional on some error shapes.
 */
function locateJsonError(text: string, error: unknown): JsonErrorLocation | undefined {
  const message = error instanceof Error ? error.message : "";

  const lineColumn = /line (\d+) column (\d+)/.exec(message);
  if (lineColumn) return { line: Number(lineColumn[1]), column: Number(lineColumn[2]) };

  const position = /position (\d+)/.exec(message);
  if (!position) return undefined;

  const offset = Math.min(Number(position[1]), text.length);
  const consumed = text.slice(0, offset).split("\n");
  return { line: consumed.length, column: (consumed[consumed.length - 1]?.length ?? 0) + 1 };
}

export function hasEventTypeAttribute(attributes: Record<string, string> | null | undefined): boolean {
  const value = attributes?.eventType;
  return typeof value === "string" && value.trim().length > 0;
}

/** Cloud Scheduler job id charset — mirrors API `ScheduledJobIdSchema`. */
const JOB_ID_PATTERN = /^[a-zA-Z][-A-Za-z0-9_]*$/;

export function isValidScheduledJobId(value: string): boolean {
  return value.length > 0 && value.length <= 500 && JOB_ID_PATTERN.test(value);
}
